import { Flex, Grid, Heading, Image, SkeletonCircle, SkeletonText, Text } from '@chakra-ui/react';
import NextLink from 'next/link';
import { FC, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { InView } from 'react-intersection-observer';

import { BackToTop } from '@/components/ui/core/BackToTop/BackToTop';
import { NoResults } from '@/components/ui/shared/messages/NoResults/NoResults';
import useElement from '@/hooks/useElement';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import {
  AnyProfile,
  getProfileFirstName,
  getProfileLastName,
  getProfileLinkSlug,
  getProfileSquareAvatar,
  getProfileTitle,
} from '@/utils/profileHelpers';

interface ILectureURLSearchParams {
  query?: string;
}

const fallbackImageUrl = '/images/landing/conferences-gradient.png';

interface IPublisherGridProps {
  items: Array<AnyProfile>;
  isLoadingMore: boolean;
  isReachingEnd: boolean;
  isEmpty: boolean;
  onLoadMore: () => void;
  error: Error;
  pageSize: number;
}

export const SpeakersGrid: FC<IPublisherGridProps> = ({
  items,
  isLoadingMore,
  isEmpty,
  isReachingEnd,
  onLoadMore,
  error,
  pageSize,
}) => {
  const { element: lectureListElement, ref: lectureListRef } = useElement();
  const { t } = useTranslation();
  const { query } = useQueryParams<ILectureURLSearchParams>({
    query: StringParam,
  });

  if (isEmpty) {
    return <NoResults title={query ? t('common.noResultsForQuery', { query }) : t('common.noResults')} />;
  }

  return (
    <Fragment>
      <Grid
        ref={lectureListRef}
        templateColumns={{
          base: 'repeat(auto-fill, minmax(min(100%, 218px), 1fr))',
          lg: 'repeat(auto-fill, minmax(min(100%, 240px), 1fr))',
        }}
        h="max-content"
      >
        {items.map((profile) => (
          <NextLink key={profile.id} href={`/speakers/${getProfileLinkSlug(profile)}`}>
            <Flex
              textAlign="center"
              alignItems="center"
              p="4"
              flexDir="column"
              w="full"
              boxSizing="border-box"
              _hover={{
                boxShadow: 'card2xl',
              }}
            >
              <Image
                borderRadius="full"
                width="200px"
                height="200px"
                src={getProfileSquareAvatar(profile) || fallbackImageUrl}
                alt={getProfileFirstName(profile)}
                objectFit="cover"
              />
              <Heading fontSize="3xl" mt="6">
                {getProfileFirstName(profile)} {getProfileLastName(profile)}
              </Heading>
              <Text fontSize="sm" color="gray.800" mt="2">
                {getProfileTitle(profile)}
              </Text>
            </Flex>
          </NextLink>
        ))}

        {isLoadingMore ? Array.from({ length: pageSize }, (_, i) => <CardSkeleton key={i} />) : null}
      </Grid>

      <InView
        as="span"
        rootMargin="200px"
        onChange={(inView) => {
          if (inView && !error && !isReachingEnd) {
            onLoadMore?.();
          }
        }}
      />

      {!isEmpty && <BackToTop parentTopOffset={lectureListElement?.offsetTop} scrollingOffset={280} />}
    </Fragment>
  );
};

function CardSkeleton() {
  return (
    <Flex flexDir="column" h="full" alignItems="center" p="4" justifyContent="space-between">
      <SkeletonCircle size="200" />
      <SkeletonText w="full" mt="4" skeletonHeight="8" noOfLines={1} />
      <SkeletonText w="full" my="4" skeletonHeight="4" noOfLines={1} />
    </Flex>
  );
}
